/**
 * Focus bucket members on the evidence canvas (the Fusion canvas, window.__fusionCy).
 * Visibility is controlled by the `_visibility` data property (R11): members
 * VISIBLE, everything else DIMMED. No node is added or removed. The Fusion
 * canvas has no undo stack, and this only changes opacity, so there is no
 * snapshot to push; `clearCanvasFocus` resets every element to VISIBLE.
 */
import type cytoscape from 'cytoscape';
import { VISIBILITY } from '../../constants/visibility';
import { useFusionStore } from '../../stores/useFusionStore';
import type { ConsolidatedBucket } from './types';

export function memberKeys(buckets: ConsolidatedBucket[]): Set<string> {
  const keys = new Set<string>();
  for (const b of buckets) {
    for (const m of b.members) {
      for (const k of [m.entity_id, m.canonical_id, m.address]) if (k) keys.add(k);
    }
  }
  return keys;
}

function ensureOpacityRule(cy: cytoscape.Core): void {
  if (cy.scratch('_consolVisRule')) return;
  cy.style().selector('node[_visibility], edge[_visibility]').style('opacity', 'data(_visibility)').update();
  cy.scratch('_consolVisRule', true);
}

/** Returns the id of the first matched node, or null when the canvas holds none of the members. */
export function focusMembersOnCanvas(cy: cytoscape.Core, keys: Set<string>): string | null {
  const isMember = (n: cytoscape.NodeSingular) => {
    const d = n.data();
    return [d.id, d.canonical_id, d.address, d.entity_id].some((k) => typeof k === 'string' && keys.has(k));
  };
  const members = cy.nodes().filter(isMember);
  if (members.empty()) return null;
  ensureOpacityRule(cy);
  cy.batch(() => {
    cy.nodes().forEach((n) => { n.data('_visibility', isMember(n) ? VISIBILITY.VISIBLE : VISIBILITY.DIMMED); });
    cy.edges().forEach((e) => {
      const both = isMember(e.source()) && isMember(e.target());
      e.data('_visibility', both ? VISIBILITY.VISIBLE : VISIBILITY.DIMMED);
    });
  });
  const first = members[0];
  cy.elements().unselect();
  first.select();
  useFusionStore.getState().select({ id: String(first.id()), kind: 'node' });
  try { cy.animate({ fit: { eles: members, padding: 80 }, duration: 200 }); } catch { /* headless */ }
  return String(first.id());
}

export function clearCanvasFocus(cy: cytoscape.Core): void {
  cy.batch(() => {
    cy.elements().forEach((el) => { if (el.data('_visibility') !== undefined) el.data('_visibility', VISIBILITY.VISIBLE); });
  });
}
